import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../lib/api';

// ── Specials / sale pricing (admin) ──
//
// The team marks products on sale here, then Publishes to push them live to
// customers. A special is a draft until published (invisible to customers); an
// edit to a live special lands in a draft so the live numbers stay put until
// re-published. The matching price math lives server-side (lib/specials.js) —
// this view just drives the draft → publish workflow.
//
// Pricing note: the preview "was/now" uses the product's LIST price. Customers
// with negotiated (tier) pricing get the discount off their own price, so they
// may pay less than the preview shows — never more.

const RED = '#A50B18';

function money(v) {
  if (v == null || isNaN(v)) return '—';
  return '$' + Number(v).toFixed(2);
}

// Compute the sale price the same way the server does, for live preview.
function previewSalePrice(listPrice, discountType, discountValue) {
  const p = Number(listPrice) || 0;
  const v = Number(discountValue);
  if (!(p > 0) || !Number.isFinite(v)) return null;
  if (discountType === 'percent') {
    if (!(v > 0)) return null;
    return Math.round(p * (1 - Math.min(v, 100) / 100) * 100) / 100;
  }
  if (discountType === 'amount') {
    if (!(v >= 0)) return null;
    return Math.round(Math.min(v, p) * 100) / 100;
  }
  return null;
}

// Human summary of a special's live or draft values.
function describeValues(d) {
  if (!d || !d.discountType) return null;
  const amt = d.discountType === 'percent' ? `${d.discountValue}% off` : `${money(d.discountValue)} sale price`;
  const aud = d.audience?.type === 'customers'
    ? `${(d.audience.customerIds || []).length} customer(s)`
    : 'all customers';
  const dates = d.startDate || d.endDate
    ? ` · ${d.startDate || 'now'} → ${d.endDate || 'no end'}`
    : '';
  return `${amt} · ${aud}${dates}`;
}

// readOnly: sales reps see what's on special (live + pending drafts) but
// can't create, publish, or remove — backend enforces via specials:'read'.
export default function SpecialsTab({ readOnly }) {
  const [products, setProducts] = useState([]);
  const [specials, setSpecials] = useState({});   // sku -> special row
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editSku, setEditSku] = useState('');     // sku whose editor is open
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cat, sp] = await Promise.all([adminApi.getCatalog(), adminApi.listSpecials()]);
      setProducts(cat.products || []);
      const bySku = {};
      for (const row of (sp.specials || [])) bySku[String(row.sku)] = row;
      setSpecials(bySku);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const productBySku = {};
  for (const p of products) productBySku[String(p.sku)] = p;

  const draftCount = Object.values(specials).filter(s => s.draft).length;
  const activeSpecials = Object.values(specials).sort((a, b) =>
    String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));

  // Search results: visible products matching the query that don't already
  // have a special (those live in the section above).
  const q = search.trim().toLowerCase();
  const searchResults = q
    ? products.filter(p =>
        p.status === 'visible' &&
        !specials[String(p.sku)] &&
        (`${p.name} ${p.sku} ${p.brand || ''}`.toLowerCase().includes(q)))
      .slice(0, 25)
    : [];

  async function doAction(fn, key) {
    setBusy(key);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
      alert('Failed: ' + e.message);
    } finally {
      setBusy('');
    }
  }

  if (loading && !products.length) return <p style={s.sub}>Loading catalog…</p>;

  return (
    <div>
      <div style={s.headerRow}>
        <div>
          <h3 style={s.title}>Specials</h3>
          <p style={s.sub}>
            {readOnly
              ? 'Current sale pricing — live specials and pending drafts.'
              : 'Mark products on sale, then Publish to push them live to customers.'}
          </p>
        </div>
        {!readOnly && (
          <button
            style={{ ...s.btnPrimary, opacity: draftCount ? 1 : 0.5 }}
            disabled={!draftCount || busy === 'publish-all'}
            onClick={() => doAction(() => adminApi.publishAllSpecials(), 'publish-all')}
          >
            {busy === 'publish-all' ? 'Publishing…' : `Publish ${draftCount || 'all'} change${draftCount === 1 ? '' : 's'}`}
          </button>
        )}
      </div>

      {error && <div style={s.errorBanner}>{error}</div>}

      {/* ── Add a special ── */}
      {!readOnly && (
        <div style={s.card}>
          <div style={s.cardTitle}>Add a special</div>
          <input
            style={s.search}
            placeholder="Search a product by name, SKU, or brand…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {q && searchResults.length === 0 && (
            <p style={s.sub}>No matching customer-visible products without a special.</p>
          )}
          {searchResults.map(p => (
            <div key={p.sku} style={s.row}>
              <div style={s.rowMain}>
                <div style={s.rowName}>{p.name}</div>
                <div style={s.rowMeta}>{p.sku} · {p.brand || '—'} · list {money(p.price)}</div>
                {!(Number(p.price) > 0) && (
                  <div style={s.noPriceNote}>No price set — a discount won't show. Set its price in SOS first.</div>
                )}
              </div>
              {editSku === p.sku ? null : (
                <button style={s.btnOutline} onClick={() => setEditSku(p.sku)}>Set sale</button>
              )}
              {editSku === p.sku && (
                <SpecialEditor
                  product={p}
                  special={null}
                  onCancel={() => setEditSku('')}
                  onSaved={async () => { setEditSku(''); setSearch(''); await load(); }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Current specials ── */}
      <div style={s.card}>
        <div style={s.cardTitle}>Current specials ({activeSpecials.length})</div>
        {activeSpecials.length === 0 && <p style={s.sub}>No specials yet. Use the search above to add one.</p>}
        {activeSpecials.map(sp => {
          const p = productBySku[String(sp.sku)];
          const live = sp.status === 'published' && sp.discountType ? sp : null;
          const liveSummary = live ? describeValues(live) : null;
          const draftSummary = sp.draft ? describeValues(sp.draft) : null;
          const editing = editSku === sp.sku;
          return (
            <div key={sp.sku} style={s.specialRow}>
              <div style={s.rowMain}>
                <div style={s.rowName}>
                  {p ? p.name : `SKU ${sp.sku}`}
                  {live
                    ? <span style={s.chipLive}>LIVE</span>
                    : <span style={s.chipDraft}>DRAFT</span>}
                  {live && sp.draft && <span style={s.chipPending}>edited · not published</span>}
                </div>
                <div style={s.rowMeta}>
                  {sp.sku}{p ? ` · list ${money(p.price)}` : ''}
                </div>
                {liveSummary && <div style={s.liveLine}>Live: {liveSummary}</div>}
                {draftSummary && <div style={s.draftLine}>Draft: {draftSummary}</div>}
              </div>
              {!editing && !readOnly && (
                <div style={s.actions}>
                  {sp.draft && (
                    <button style={s.btnPrimary} disabled={busy === sp.sku}
                      onClick={() => doAction(() => adminApi.publishSpecial(sp.sku), sp.sku)}>
                      Publish
                    </button>
                  )}
                  <button style={s.btnOutline} onClick={() => setEditSku(sp.sku)}>Edit</button>
                  {live && (
                    <button style={s.btnOutline} disabled={busy === sp.sku}
                      onClick={() => doAction(() => adminApi.unpublishSpecial(sp.sku), sp.sku)}>
                      Take down
                    </button>
                  )}
                  <button style={s.btnDanger} disabled={busy === sp.sku}
                    onClick={() => { if (window.confirm('Remove this special entirely?')) doAction(() => adminApi.deleteSpecial(sp.sku), sp.sku); }}>
                    Remove
                  </button>
                </div>
              )}
              {editing && (
                <SpecialEditor
                  product={p || { sku: sp.sku, name: `SKU ${sp.sku}`, price: 0 }}
                  special={sp}
                  onCancel={() => setEditSku('')}
                  onSaved={async () => { setEditSku(''); await load(); }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Editor for one product's special ──
function SpecialEditor({ product, special, onCancel, onSaved }) {
  // Prefill from the draft (if mid-edit) else the live values else blank.
  const init = special?.draft || (special?.discountType ? special : null);
  const [discountType] = useState(init?.discountType || 'percent'); // percent-only in v1
  const [discountValue, setDiscountValue] = useState(init?.discountValue != null ? String(init.discountValue) : '');
  const [label, setLabel] = useState(init?.label || '');
  const [startDate, setStartDate] = useState(init?.startDate || '');
  const [endDate, setEndDate] = useState(init?.endDate || '');
  const [audienceType, setAudienceType] = useState(init?.audience?.type || 'all');
  const [customers, setCustomers] = useState(
    (init?.audience?.type === 'customers' ? (init.audience.customerIds || []) : []).map(id => ({ id: String(id), name: `Customer ${id}` }))
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const preview = previewSalePrice(product.price, discountType, discountValue);
  // A percent discount on a $0-priced product produces no visible sale, so the
  // special would silently never show on the customer shelf. Block saving it.
  const noPrice = !(Number(product.price) > 0);

  async function save() {
    setErr('');
    if (noPrice) { setErr('This product has no price, so a discount can’t apply.'); return; }
    const v = Number(discountValue);
    if (!Number.isFinite(v) || v < 0) { setErr('Enter a valid discount value.'); return; }
    if (discountType === 'percent' && v > 100) { setErr('Percent cannot exceed 100.'); return; }
    if (startDate && endDate && endDate < startDate) { setErr('End date must be on or after start date.'); return; }
    if (audienceType === 'customers' && customers.length === 0) { setErr('Pick at least one customer, or choose All customers.'); return; }
    const body = {
      discountType,
      discountValue: v,
      label: label.trim() || null,
      startDate: startDate || null,
      endDate: endDate || null,
      audience: audienceType === 'customers'
        ? { type: 'customers', customerIds: customers.map(c => c.id) }
        : { type: 'all' },
    };
    setSaving(true);
    try {
      await adminApi.setSpecial(product.sku, body);
      await onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={s.editor}>
      {noPrice && (
        <div style={s.warnBanner}>
          <strong>{product.name}</strong> has no price ({money(product.price)}) in the catalog, so a discount won’t show customers anything. Set its price in SOS first, then re-sync the catalog.
        </div>
      )}
      <div style={s.editorGrid}>
        <label style={s.field}>
          <span style={s.fieldLabel}>Percent off (%)</span>
          <input style={s.input} type="number" min="1" max="100" step="1"
            value={discountValue} onChange={e => setDiscountValue(e.target.value)} />
        </label>
        <label style={s.field}>
          <span style={s.fieldLabel}>Badge text (optional)</span>
          <input style={s.input} placeholder="SALE" value={label} onChange={e => setLabel(e.target.value)} maxLength={40} />
        </label>
        <label style={s.field}>
          <span style={s.fieldLabel}>Start date (optional)</span>
          <input style={s.input} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label style={s.field}>
          <span style={s.fieldLabel}>End date (optional)</span>
          <input style={s.input} type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
      </div>

      <div style={s.audienceBlock}>
        <span style={s.fieldLabel}>Who sees it</span>
        <label style={s.radio}>
          <input type="radio" checked={audienceType === 'all'} onChange={() => setAudienceType('all')} /> All customers
        </label>
        <label style={s.radio}>
          <input type="radio" checked={audienceType === 'customers'} onChange={() => setAudienceType('customers')} /> Specific customers
        </label>
        {audienceType === 'customers' && (
          <CustomerPicker selected={customers} onChange={setCustomers} />
        )}
      </div>

      {preview != null && Number(product.price) > 0 && (
        <div style={s.preview}>
          Preview (off list price): <s style={{ color: '#999' }}>{money(product.price)}</s>{' '}
          <strong style={{ color: RED }}>{money(preview)}</strong>
          {preview >= Number(product.price) && <span style={{ color: '#b26500' }}> — this would not lower the price</span>}
        </div>
      )}

      {err && <div style={s.errorBanner}>{err}</div>}

      <div style={s.editorActions}>
        <button style={{ ...s.btnPrimary, opacity: (saving || noPrice) ? 0.5 : 1 }} disabled={saving || noPrice} onClick={save}>{saving ? 'Saving…' : 'Save draft'}</button>
        <button style={s.btnOutline} disabled={saving} onClick={onCancel}>Cancel</button>
        <span style={s.hint}>{noPrice ? 'Set a price in SOS before adding a special.' : 'Saving creates/updates a draft. Use Publish to make it live.'}</span>
      </div>
    </div>
  );
}

// ── Searchable customer multi-select ──
function CustomerPicker({ selected, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await adminApi.listCustomers({ search: query.trim() });
        setResults(res.customers || []);
        setOpen(true);
      } catch { /* ignore search errors */ }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query]);

  const selectedIds = new Set(selected.map(c => String(c.id)));

  function add(c) {
    if (selectedIds.has(String(c.id))) return;
    onChange([...selected, { id: String(c.id), name: c.name || `Customer ${c.id}` }]);
    setQuery('');
    setResults([]);
    setOpen(false);
  }
  function remove(id) {
    onChange(selected.filter(c => String(c.id) !== String(id)));
  }

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <div style={s.chipWrap}>
        {selected.map(c => (
          <span key={c.id} style={s.custChip}>
            {c.name}
            <button style={s.chipX} onClick={() => remove(c.id)} aria-label="Remove">×</button>
          </span>
        ))}
        {selected.length === 0 && <span style={s.sub}>No customers selected yet.</span>}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          style={s.input}
          placeholder="Search customers to add…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
        />
        {open && results.length > 0 && (
          <div style={s.dropdown}>
            {results.map(c => (
              <button key={c.id} style={s.dropdownItem} onClick={() => add(c)}>
                {c.name} <span style={s.sub}>#{c.id}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' },
  title: { fontSize: '1.1rem', fontWeight: 700, color: '#222', margin: '0 0 0.25rem' },
  sub: { color: '#888', fontSize: '0.85rem', margin: 0 },
  hint: { color: '#999', fontSize: '0.78rem' },
  card: { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem' },
  cardTitle: { fontSize: '0.95rem', fontWeight: 600, color: '#333', marginBottom: '0.75rem' },
  search: { width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.9rem', outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0', borderTop: '1px solid #f3f3f3', flexWrap: 'wrap', gap: '0.5rem' },
  specialRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '0.75rem 0', borderTop: '1px solid #f3f3f3', flexWrap: 'wrap', gap: '0.6rem' },
  rowMain: { flex: 1, minWidth: 220 },
  rowName: { fontSize: '0.92rem', fontWeight: 600, color: '#222', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  rowMeta: { fontSize: '0.8rem', color: '#888', marginTop: '0.15rem' },
  liveLine: { fontSize: '0.82rem', color: '#2e7d32', marginTop: '0.3rem' },
  draftLine: { fontSize: '0.82rem', color: '#b26500', marginTop: '0.15rem' },
  actions: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  chipLive: { background: '#e6f4ea', color: '#2e7d32', borderRadius: 6, padding: '0.1rem 0.4rem', fontSize: '0.7rem', fontWeight: 700 },
  chipDraft: { background: '#fff3e0', color: '#b26500', borderRadius: 6, padding: '0.1rem 0.4rem', fontSize: '0.7rem', fontWeight: 700 },
  chipPending: { background: '#fdecea', color: RED, borderRadius: 6, padding: '0.1rem 0.4rem', fontSize: '0.68rem', fontWeight: 600 },
  btnPrimary: { background: RED, color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { background: 'transparent', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' },
  btnDanger: { background: 'transparent', color: RED, border: '1px solid #f0c4c8', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' },
  errorBanner: { background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#a32d2d' },
  warnBanner: { background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '0.9rem', fontSize: '0.85rem', color: '#8a5a00' },
  noPriceNote: { fontSize: '0.78rem', color: '#b26500', marginTop: '0.2rem' },
  editor: { width: '100%', marginTop: '0.75rem', padding: '1rem', background: '#fafafa', border: '1px solid #eee', borderRadius: 10 },
  editorGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  fieldLabel: { fontSize: '0.78rem', fontWeight: 600, color: '#555' },
  input: { padding: '0.5rem 0.6rem', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.85rem', outline: 'none', background: '#fff', boxSizing: 'border-box', width: '100%' },
  audienceBlock: { marginTop: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  radio: { fontSize: '0.85rem', color: '#333', display: 'flex', alignItems: 'center', gap: '0.4rem' },
  preview: { marginTop: '0.9rem', fontSize: '0.88rem', color: '#444' },
  editorActions: { marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem' },
  custChip: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#eef2ff', color: '#3340a0', borderRadius: 14, padding: '0.2rem 0.6rem', fontSize: '0.8rem' },
  chipX: { background: 'none', border: 'none', color: '#3340a0', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: 0 },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 8, marginTop: 2, zIndex: 20, maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
  dropdownItem: { display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.7rem', background: 'none', border: 'none', borderBottom: '1px solid #f3f3f3', cursor: 'pointer', fontSize: '0.85rem', color: '#333' },
};
